import type { StorageUsage } from '~/gql/storage/storage'
import { useMutation, useQuery } from '@apollo/client/react'
import { Loader, Play, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_DELETE_SCORM_MODULE, GQL_SCORM_MODULES } from '~/gql/scorm-modules/scorm-modules'
import { GQL_STORAGE_USAGE } from '~/gql/storage/storage'
import { env } from '~/helpers/env'
import { cn } from '~/lib/utils'
import StorageUsageCard from '~/pages/backoffice/admin/modules/StorageUsageCard'
import { formatBytes } from '~/utils/bytes'

const STATUS_STYLES: Record<string, string> = {
  READY: 'bg-green-500/10 text-green-600',
  PROCESSING: 'bg-amber-500/10 text-amber-600',
  FAILED: 'bg-red-500/10 text-red-600',
}

const STATUS_KEYS: Record<string, string> = {
  READY: 'modules.status.ready',
  PROCESSING: 'modules.status.processing',
  FAILED: 'modules.status.failed',
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const className = STATUS_STYLES[status] ?? 'bg-muted text-muted-foreground'
  const label = STATUS_KEYS[status] ? t(STATUS_KEYS[status]) : status
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>{label}</span>
}

/**
 * Refus de quota renvoyé par `/modules/upload` (#180). L'API porte le code et
 * les trois chiffres dans le corps de la réponse plutôt qu'une phrase toute
 * faite : c'est ce qui permet de dire le refus dans la langue de
 * l'utilisateur, là où le `message` de l'API est en français seul.
 */
type UploadErrorPayload = {
  message?: string
  code?: string
  usedBytes?: number
  quotaBytes?: number
  incomingBytes?: number
}

function ModulesTable() {
  const { t, i18n } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_SCORM_MODULES, {
    fetchPolicy: 'cache-and-network',
  })
  const { data: storageData, refetch: refetchStorage } = useQuery(GQL_STORAGE_USAGE, {
    fetchPolicy: 'cache-and-network',
  })
  const [deleteModule] = useMutation(GQL_DELETE_SCORM_MODULE)
  const [title, setTitle] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const modules = data?.scormModules ?? []
  const storageUsage = storageData?.storageUsage as unknown as StorageUsage | undefined

  /**
   * Le refus de quota est reformulé ici, avec ses trois chiffres : ce qui est
   * occupé, ce qui est autorisé, et quoi faire. Les autres erreurs gardent le
   * message de l'API, qui est déjà spécifique au package en défaut.
   */
  const uploadErrorMessage = (payload: UploadErrorPayload | null): string => {
    if (payload?.code === 'STORAGE_QUOTA_EXCEEDED' && payload.quotaBytes != null) {
      return t('storage.uploadRefused', {
        used: formatBytes(payload.usedBytes ?? 0, i18n.language),
        quota: formatBytes(payload.quotaBytes, i18n.language),
        incoming: formatBytes(payload.incomingBytes ?? 0, i18n.language),
      })
    }
    return payload?.message ?? t('modules.uploadFailed')
  }

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0]
    if (!file) {
      toast.error(t('modules.selectFile'))
      return
    }
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('title', title)
      fd.append('file', file)
      const res = await fetch(`${env.api.url}/modules/upload`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      })
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as UploadErrorPayload | null
        throw new Error(uploadErrorMessage(payload))
      }
      toast.success(t('modules.imported'))
      setTitle('')
      setFileName(null)
      if (fileRef.current) fileRef.current.value = ''
      await Promise.all([refetch(), refetchStorage()])
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('modules.uploadFailed'))
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteModule({ variables: { id } })
      toast.success(t('modules.deleted'))
      // La suppression libère le disque : le compteur doit suivre tout de suite.
      await Promise.all([refetch(), refetchStorage()])
    } catch {
      toast.error(t('modules.deleteFailed'))
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-4">
      {/* Avant le formulaire d'import : ce qu'il reste se lit AVANT de choisir
          un fichier, pas après le refus. */}
      <StorageUsageCard usage={storageUsage} />

      <Card>
        <CardHeader>
          <CardTitle>{t('modules.import.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex flex-1 flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="module-title">{t('common.title')}</label>
            <Input
              id="module-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={t('modules.titlePlaceholder')}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="module-file">{t('modules.package')}</label>
            <input
              id="module-file"
              ref={fileRef}
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              onChange={e => setFileName(e.target.files?.[0]?.name ?? null)}
            />
            <Button
              type="button"
              variant="outline"
              className={cn('justify-start overflow-hidden font-normal', !fileName && 'text-muted-foreground')}
              onClick={() => fileRef.current?.click()}
            >
              <span className="truncate">{fileName ?? t('modules.chooseFile')}</span>
            </Button>
          </div>
          <Button onClick={handleUpload} disabled={uploading}>
            {uploading ? <Loader className="mr-2 size-4 animate-spin" /> : <Upload className="mr-2 size-4" />}
            {t('common.import')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('modules.list.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && modules.length === 0
            ? (
                <div className="flex justify-center p-6">
                  <Loader className="size-6 animate-spin" />
                </div>
              )
            : modules.length === 0
              ? <p className="p-4 text-center text-sm text-muted-foreground">{t('modules.empty')}</p>
              : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('common.title')}</TableHead>
                        <TableHead>{t('common.version')}</TableHead>
                        <TableHead className="text-right">{t('storage.size')}</TableHead>
                        <TableHead>{t('common.status')}</TableHead>
                        <TableHead className="text-right">{t('common.action')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {modules.map(m => (
                        <TableRow key={m.id}>
                          <TableCell className="font-medium">{m.title}</TableCell>
                          <TableCell>{m.scormVersion ?? '—'}</TableCell>
                          {/* Un module FAILED est à zéro octet : ses fichiers ont
                              été effacés, il ne consomme rien. */}
                          <TableCell className="text-right tabular-nums text-muted-foreground">
                            {formatBytes(m.sizeBytes, i18n.language)}
                          </TableCell>
                          <TableCell><StatusBadge status={m.status} /></TableCell>
                          <TableCell className="text-right">
                            {m.status === 'READY' && (
                              <Button variant="ghost" size="icon" asChild aria-label={t('modules.preview')}>
                                <Link to={`/scorm/preview/${m.id}`}>
                                  <Play className="size-4" />
                                </Link>
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(m.id)} aria-label={t('common.delete')}>
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
        </CardContent>
      </Card>
    </div>
  )
}

export default ModulesTable
